import { Button, Card, CardBody, CardFooter, Typography } from "@material-tailwind/react";
import { Modal } from "@mui/material";
import AsyncSelect from "react-select/async";
import { useState } from "react";
import toast from "react-hot-toast";
import { TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { t } from "i18next";
import { api } from "../../../../../../../services/api/api";
import { showApiErrorToast } from "../../../../../../../services/api/error";
import Loading from "../../../../../../../components/loading/Loading";

export default function DeleteReportTemplateModal({
  open,
  template,
  allTemplates,
  onDelete,
  onClose
}: {
  open: boolean,
  template: EasyTech.ReportTemplate,
  allTemplates: EasyTech.ReportTemplate[],
  onDelete: () => void,
  onClose: () => void
}) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [newDefaultId, setNewDefaultId] = useState<string | null>(null);
  const [selectedOption, setSelectedOption] = useState<{ value: string; label: string } | null>(null);

  const otherTemplates = allTemplates.filter(tp => tp.id !== template.id);
  const isOnlyTemplate = otherTemplates.length === 0;

  const selectOptions = otherTemplates.map(tp => ({
    value: tp.id as string,
    label: tp.name
  }));

  const loadOptions = (inputValue: string, callback: (options: any[]) => void) => {
    const filtered = selectOptions.filter(i =>
      i.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filtered);
  };

  const handleConfirmDelete = async () => {
    setIsLoading(true);
    try {
      if (template.isDefault) {
        if (isOnlyTemplate) {
          setIsLoading(false);
          return;
        }

        if (!newDefaultId) {
          toast.error(t("settings:reports:templates:delete:selectDefaultError", "Please select a replacement default report."));
          setIsLoading(false);
          return;
        }

        // 1. Mettre à jour le nouveau template par défaut
        const newDefaultTemplate = allTemplates.find(tp => tp.id === newDefaultId);
        if (newDefaultTemplate) {
          const updatedTemplate = { ...newDefaultTemplate, isDefault: true };
          await api.put(`admin/report-templates/${newDefaultId}`, updatedTemplate);
        }
      }

      // 2. Supprimer le template actuel
      await api.delete(`admin/report-templates/${template.id}`);

      toast.success(t("settings:reports:templates:delete:success", "Report template deleted successfully"));
      onDelete();
      onClose();
    } catch (error) {
      console.error(error);
      showApiErrorToast(error, { fallbackMessage: "Error while deleting report template" });
    } finally {
      setIsLoading(false);
    }
  };

  const isConfirmDisabled = template.isDefault && (!newDefaultId || isOnlyTemplate);

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="te-modal"
      aria-labelledby="te-modal"
      aria-describedby="te-modal"
    >
      <Card className="te-modal-content rounded-lg w-full max-w-md mx-auto">
        <button className="close-button" onClick={onClose}>
          <XMarkIcon className="w-4" />
        </button>
        <CardBody>
          <div className="flex items-center gap-3 mb-5">
            <div className="rounded-full p-2 bg-red-50">
              <TrashIcon className="w-7 text-red-500" />
            </div>
            <Typography variant="h5">
              {t("settings:reports:templates:delete:title", "Delete {{name}}", { name: template.name })} {template.isDefault ? "(default)" : ""}
            </Typography>
          </div>

          <Typography variant="paragraph" className="text-gray-700 text-sm mb-4">
            {t("settings:reports:templates:delete:confirmText", "You are about to delete this report permanently, it will be replaced by the default report for all customers using this report. Do you want to perform this action?")}
          </Typography>

          {template.isDefault && (
            <div className="flex flex-col gap-2 mt-4">
              <Typography variant="h6" className="text-xs font-semibold text-gray-600 uppercase">
                {t("settings:reports:templates:delete:defaultReportLabel", "Default report")}
              </Typography>
              {isOnlyTemplate ? (
                <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-700 mt-0.5 flex-shrink-0" />
                    <div className="flex flex-col">
                      <Typography className="text-sm font-bold text-gray-500">
                        {t("settings:reports:templates:delete:cannotDeleteOnlyDefaultTitle", "Suppression impossible")}
                      </Typography>
                      <Typography className="text-xs text-gray-500 mt-1">
                        {t("settings:reports:templates:delete:cannotDeleteOnlyDefaultDesc", "Ce rapport est l'unique modèle disponible. Vous ne pouvez pas le supprimer car un modèle par défaut est obligatoire.")}
                      </Typography>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 border border-orange-200 bg-orange-50 rounded-lg">
                  <div className="flex items-start gap-2 mb-4">
                    <ExclamationTriangleIcon className="h-5 w-5 text-orange-700 mt-0.5 flex-shrink-0" />
                    <div className="flex flex-col">
                      <Typography className="text-sm font-bold text-gray-500">
                        {t("settings:reports:templates:delete:replaceDefault", "Replace the default report")}
                      </Typography>
                      <Typography className="text-xs text-gray-500">
                        {t("settings:reports:templates:delete:replaceDefaultDesc", "Before deleting the default report you must configure another one instead.")}
                      </Typography>
                    </div>
                  </div>

                  <div className="w-full select-white-bg">
                    <style>{`
                      .select-white-bg .react-select__control {
                        background-color: #ffffff !important;
                      }
                    `}</style>
                    <AsyncSelect
                      cacheOptions
                      defaultOptions={selectOptions}
                      loadOptions={loadOptions}
                      value={selectedOption}
                      onChange={(opt: any) => {
                        setSelectedOption(opt);
                        setNewDefaultId(opt ? opt.value : null);
                      }}
                      classNamePrefix="react-select"
                      placeholder={t("settings:reports:templates:delete:selectDefault", "Select a default report")}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          backgroundColor: "#ffffff",
                        }),
                        menu: (provided) => ({
                          ...provided,
                          backgroundColor: "#ffffff",
                        })
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </CardBody>
        <CardFooter className="pt-0 flex gap-2 justify-end">
          <Button variant="outlined" onClick={onClose} className="normal-case !border !border-[#B6BAC3] text-gray-700">
            {t("common:cancel", "Cancel")}
          </Button>

          {isLoading ? (
            <Loading />
          ) : (
            <Button
              color={isConfirmDisabled ? "blue-gray" : "red"}
              onClick={handleConfirmDelete}
              disabled={isConfirmDisabled}
              className="normal-case"
            >
              {t("common:confirm", "Confirm")}
            </Button>
          )}
        </CardFooter>
      </Card>
    </Modal>
  );
}
